import { Turnstile } from "@marsidev/react-turnstile";
import { useState } from "react";
import { Linking, Pressable, StyleSheet, View } from "react-native";
import Svg, { Path } from "react-native-svg";

import { GText } from "@/components/g-text";
import { colors } from "@/constants/colors";

import PhoneLogin from "./phone-login";

interface Props {
  onClose: () => void;
  onSignedIn: () => void;
}

type Step = "phone" | "terms";

export default function SignInModalContent({ onClose, onSignedIn }: Props) {
  const [step, setStep] = useState<Step>("terms");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [smsConsent, setSmsConsent] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<null | string>(null);

  function handleClose() {
    setStep("terms");
    setTermsAccepted(false);
    setSmsConsent(false);
    setTurnstileToken(null);
    onClose();
  }

  function handleContinue() {
    if (termsAccepted && smsConsent) setStep("phone");
  }

  function handleVerified() {
    setStep("terms");
    setTermsAccepted(false);
    setSmsConsent(false);
    onSignedIn();
  }

  if (step === "terms" || !turnstileToken) {
    return (
      <View>
        <View style={styles.checkboxRow}>
          <Pressable
            onPress={() => setTermsAccepted((v) => !v)}
            style={[styles.checkbox, termsAccepted && styles.checkboxChecked]}
          >
            {termsAccepted && (
              <Svg fill="none" height={18} viewBox="0 0 24 24" width={18}>
                <Path
                  d="M5 13l4 4L19 7"
                  stroke={colors.black}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={3}
                />
              </Svg>
            )}
          </Pressable>
          <GText style={styles.termsLabel}>
            i agree to the{" "}
            <GText
              onPress={() => Linking.openURL("https://aglow.life/terms")}
              style={styles.termsLink}
            >
              terms of service
            </GText>
          </GText>
        </View>

        <View style={[styles.checkboxRow, styles.smsConsentRow]}>
          <Pressable
            onPress={() => setSmsConsent((v) => !v)}
            style={[styles.checkbox, smsConsent && styles.checkboxChecked]}
          >
            {smsConsent && (
              <Svg fill="none" height={18} viewBox="0 0 24 24" width={18}>
                <Path
                  d="M5 13l4 4L19 7"
                  stroke={colors.black}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={3}
                />
              </Svg>
            )}
          </Pressable>
          <GText style={styles.termsLabel}>
            i agree to receive SMS messages from aglow. message and data rates
            may apply. message frequency varies. reply STOP to opt out or HELP
            for help.{" "}
            <GText
              onPress={() => Linking.openURL("https://aglow.life/privacy")}
              style={styles.termsLink}
            >
              privacy policy
            </GText>
          </GText>
        </View>

        <View style={styles.turnstileContainer}>
          <Turnstile
            onSuccess={setTurnstileToken}
            options={{ theme: "dark" }}
            siteKey={process.env.EXPO_PUBLIC_TURNSTILE_SITE_KEY!}
          />
        </View>

        <Pressable
          disabled={!termsAccepted || !smsConsent || !turnstileToken}
          onPress={handleContinue}
          style={[
            styles.btn,
            (!termsAccepted || !smsConsent || !turnstileToken) &&
              styles.btnDisabled,
          ]}
        >
          <GText style={styles.btnText}>continue</GText>
        </Pressable>
      </View>
    );
  }

  return (
    <PhoneLogin
      captchaToken={turnstileToken}
      embedded
      onClose={handleClose}
      onVerified={handleVerified}
      visible
    />
  );
}

const styles = StyleSheet.create({
  btn: {
    alignItems: "center",
    backgroundColor: colors.white,
    borderRadius: 28,
    marginTop: 16,
    paddingVertical: 14,
    width: "100%",
  },
  btnDisabled: {
    opacity: 0.5,
  },
  btnText: {
    color: colors.black,
    fontSize: 16,
    fontWeight: "600",
  },
  checkbox: {
    alignItems: "center",
    borderColor: colors.white,
    borderRadius: 6,
    borderWidth: 2,
    height: 34,
    justifyContent: "center",
    width: 34,
  },
  checkboxChecked: {
    backgroundColor: colors.white,
    borderColor: colors.white,
  },
  checkboxRow: {
    alignItems: "center",
    flexDirection: "row",
    gap: 12,
  },
  smsConsentRow: {
    marginTop: 16,
  },
  termsLabel: {
    color: colors.light,
    flex: 1,
    fontSize: 16,
  },
  termsLink: {
    color: colors.white,
    textDecorationLine: "underline",
  },
  turnstileContainer: {
    alignItems: "center",
    marginTop: 16,
  },
});
